import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { dayColor, dayPoints, directions, type Point, type Proposal } from "../../api/routing";
import { SATELLITE_TILES, STREET_TILES, loadLeaflet, pin, type LLayerGroup, type LMap, type Leaflet } from "./leaflet";
import type { Picking } from "./StartStep";

const BASEMAP_KEY = "prv_routing_basemap";

/** The route map: the depot, the meeting point and every proposed stop in its
 * day's colour, each day's line on the road where the routing service answers
 * and dashed stop-to-stop where it does not. While a point is being picked a
 * click on the map sets it. Drawn from the same proposal as the day cards, so
 * the two cannot disagree. */
export default function PlannerMap({
  origin,
  meeting,
  proposal,
  picking,
  onPicked,
}: {
  origin: Point | null;
  meeting: Point | null;
  proposal: Proposal | null;
  picking: Picking;
  onPicked: (p: Point) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const mapRef = useRef<{ L: Leaflet; map: LMap; sites: LLayerGroup; route: LLayerGroup } | null>(null);
  const pickingRef = useRef(picking);
  pickingRef.current = picking;
  const onPickedRef = useRef(onPicked);
  onPickedRef.current = onPicked;
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || box.current === null || mapRef.current !== null) {
          return;
        }
        const map = L.map(box.current, { zoomControl: true }).setView([-34.07, 18.4], 11);
        const street = L.tileLayer(STREET_TILES, { maxZoom: 19, attribution: "© OpenStreetMap contributors" });
        const satellite = L.tileLayer(SATELLITE_TILES, { maxZoom: 19, maxNativeZoom: 18, attribution: "Imagery © Esri, Maxar, Earthstar Geographics" });
        let choice: string | null = null;
        try {
          choice = window.localStorage.getItem(BASEMAP_KEY);
        } catch {
          choice = null;
        }
        (choice === "Satellite" ? satellite : street).addTo(map);
        L.control.layers({ Street: street, Satellite: satellite }, {}, { position: "topright" }).addTo(map);
        map.on("baselayerchange", (e) => {
          try {
            window.localStorage.setItem(BASEMAP_KEY, e.name ?? "Street");
          } catch {
            // Storage refused; the choice lasts the page.
          }
        });
        map.on("click", (e) => {
          if (pickingRef.current === null) {
            return;
          }
          onPickedRef.current({ lat: e.latlng.lat, lon: e.latlng.lng, label: `${e.latlng.lat.toFixed(6)}, ${e.latlng.lng.toFixed(6)}` });
        });
        mapRef.current = { L, map, route: L.layerGroup().addTo(map), sites: L.layerGroup().addTo(map) };
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
      mapRef.current?.map.remove();
      mapRef.current = null;
    };
  }, []);

  const proposalKey = useMemo(() => (proposal ? JSON.stringify(proposal.days.map((d) => d.stops.map((s) => s.site_id))) : ""), [proposal]);

  useEffect(() => {
    const current = mapRef.current;
    if (!ready || current === null) {
      return;
    }
    const { L, map, sites, route } = current;
    sites.clearLayers();
    route.clearLayers();
    let cancelled = false;
    const frame: [number, number][] = [];
    if (origin) {
      L.marker([origin.lat, origin.lon], { icon: pin(L, "#101E1C", "S", 24) }).bindPopup(`<b>Start</b><div>${escapeHtml(origin.label)}</div>`).addTo(sites);
      frame.push([origin.lat, origin.lon]);
    }
    if (meeting) {
      L.marker([meeting.lat, meeting.lon], { icon: pin(L, "#B45309", "M", 24) }).bindPopup(`<b>Meeting point</b><div>${escapeHtml(meeting.label)}</div>`).addTo(sites);
      frame.push([meeting.lat, meeting.lon]);
    }
    if (proposal) {
      for (const day of proposal.days) {
        const color = dayColor(day.day_index);
        for (const s of day.stops) {
          L.marker([s.lat, s.lon], { icon: pin(L, color, String(s.seq), 24) }).bindPopup(`<b>${escapeHtml(s.name)}</b>`).addTo(sites);
          frame.push([s.lat, s.lon]);
        }
        const points = dayPoints(proposal, day);
        void directions(points).then((geo) => {
          if (cancelled) {
            return;
          }
          L.polyline(geo ?? points, { color, weight: 4, opacity: 0.78, dashArray: geo ? null : "6 5" }).addTo(route);
        });
      }
    }
    if (frame.length === 1) {
      map.setView(frame[0], 12);
    } else if (frame.length > 1) {
      map.fitBounds(L.latLngBounds(frame).pad(0.12));
    }
    return () => {
      cancelled = true;
    };
  }, [ready, origin, meeting, proposal, proposalKey]);

  return (
    // The picking style sits on the wrapper: the map element's own classes are
    // the map library's, and React rewriting them took away the one that keeps
    // the tiles inside the frame.
    <div className={picking ? "planner-map-wrap picking" : "planner-map-wrap"}>
      <div ref={box} className="planner-map" role="img" aria-label="Route map" />
      {failed ? <p className="notice warn">The map could not be loaded. Planning still works; the days are listed below.</p> : null}
      {proposal ? (
        <ul className="planner-map-key" aria-label="Map key">
          {proposal.days.map((d) => (
            <li key={d.day_index}>
              <span className="day-swatch" style={{ "--day": dayColor(d.day_index) } as CSSProperties} /> Day {d.day_index + 1}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** A site name is data from an upload; a popup is HTML. */
function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}
